import React from "react";
import t from "../../../../i18n";
import validateInterface from "@ui/lib/validateInterface";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { color } from "@ui";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import testSerieData from "@ui/Highcharts/lib/testSerieData";
import Marker from "@ui/Highcharts/ui/Marker";
import { PointSymbol } from "@ui/Highcharts/ui/PointSymbol";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import { quantityColor } from "../../useSelectedQuantities";
import { GapPointMetadata } from "../PointMetadata";
import { INVALID_COLOR } from "../series/getMeasureSeries";

interface P {
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  period: Period;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesFlagsOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesScatterOptions
  )[];
}

function hasSymptomFn(p: Highcharts.PointOptionsObject) {
  const metadata = validateInterface(GapPointMetadata, p.custom);
  return p.y !== null && Boolean(metadata.isSymptom);
}

function hasNoSymptomFn(p: Highcharts.PointOptionsObject) {
  const metadata = validateInterface(GapPointMetadata, p.custom);
  return p.y !== null && metadata.isSymptom === false;
}

function SymptomsLegend({ series, selectedQuantities, period }: P) {
  const hasNoSymptom = React.useMemo(
    () =>
      selectedQuantities.some(selectedQuantity => {
        const serie = series.find(
          s => s.name === selectedQuantity.quantity.name,
        ) as Highcharts.SeriesLineOptions;
        return testSerieData(serie, period, hasNoSymptomFn);
      }),
    [selectedQuantities, period, series],
  );
  const hasSymptom = React.useMemo(
    () =>
      selectedQuantities.some(selectedQuantity => {
        const serie = series.find(
          s => s.name === selectedQuantity.quantity.name,
        ) as Highcharts.SeriesLineOptions;
        return testSerieData(serie, period, hasSymptomFn);
      }),
    [selectedQuantities, series, period],
  );

  const symptomColor =
    selectedQuantities.length > 1
      ? color.raw.titanium600
      : quantityColor(selectedQuantities[0].colorIndex);

  return (
    <>
      {hasNoSymptom && (
        <Marker
          name={t("symptoms.chart.legend.no_symptom.name")}
          description={t("symptoms.chart.legend.no_symptom.description")}
        >
          <PointSymbol height={9} width={9} color={symptomColor} />
        </Marker>
      )}
      {hasSymptom && (
        <Marker
          name={t("symptoms.chart.legend.symptom.name")}
          description={t("symptoms.chart.legend.symptom.description")}
        >
          <PointSymbol height={9} width={9} color={INVALID_COLOR} />
        </Marker>
      )}
    </>
  );
}

export default React.memo(SymptomsLegend);
